import { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../../config';
import type { Manual, CreateManualRequest, UpdateManualRequest } from '../../../infrastructure/ManualClient';
import { ManualClient } from '../../../infrastructure/ManualClient';

export const useManual = () => {
  const config = getConfig();
  const manualClient = new ManualClient(config.apiUrl);
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const fetchManuals = useCallback(async (): Promise<Manual[]> => {
    try {
      return await manualClient.listManuals();
    } catch (error) {
      console.error('Failed to fetch manuals:', error);
      setError('Failed to fetch manuals');
      throw error;
    }
  }, [manualClient]);

  const getManual = useCallback(
    async (manualId: string): Promise<Manual> => {
      try {
        return await manualClient.getManual(manualId);
      } catch (error) {
        console.error(`Failed to fetch manual with ID ${manualId}:`, error);
        setError(`Failed to fetch manual with ID ${manualId}`);
        throw error;
      }
    },
    [manualClient],
  );

  const createManual = useCallback(
    async (request: CreateManualRequest): Promise<Manual> => {
      try {
        const response = await manualClient.createManual(request);
        return response;
      } catch (error) {
        console.error('Failed to create manual:', error);
        setError('Failed to create manual');
        throw error;
      }
    },
    [manualClient],
  );

  const updateManual = useCallback(
    async ({ manualId, data }: { manualId: string; data: UpdateManualRequest }): Promise<Manual> => {
      try {
        const response = await manualClient.updateManual(manualId, data);
        return response;
      } catch (error) {
        console.error(`Failed to update manual with ID ${manualId}:`, error);
        setError(`Failed to update manual with ID ${manualId}`);
        throw error;
      }
    },
    [manualClient],
  );

  const deleteManual = useCallback(
    async (manualId: string): Promise<void> => {
      try {
        await manualClient.deleteManual(manualId);
      } catch (error) {
        console.error(`Failed to delete manual with ID ${manualId}:`, error);
        setError(`Failed to delete manual with ID ${manualId}`);
        throw error;
      }
    },
    [manualClient],
  );

  const manualsQuery = useQuery({
    queryKey: ['manuals'],
    queryFn: fetchManuals,
    staleTime: 60000, // 1 minute
  });

  const createManualMutation = useMutation({
    mutationFn: createManual,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manuals'] });
    },
  });

  const updateManualMutation = useMutation({
    mutationFn: updateManual,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['manuals'] });
      queryClient.invalidateQueries({ queryKey: ['manual', variables.manualId] });
    },
  });

  const deleteManualMutation = useMutation({
    mutationFn: deleteManual,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manuals'] });
    },
  });

  return {
    error,
    setError,
    manualsQuery,
    getManual,
    createManualMutation,
    updateManualMutation,
    deleteManualMutation,
  };
};
